import { AxiosError, AxiosResponse } from 'axios';
import { AxiosStatusResponse, ReferencesParams, StopCompleteParams } from '@/models/main';
import { BookingError, JobLockByUserError, JobLockError, ValidationAPIError } from '@/helpers/throwables';
import { JobCancelParams, JobFutileParams } from '@/models/jobDetailed';
import {
  JobComplianceDataFetchResponse,
  JobComplianceDriverAllocateParams,
  JobComplianceProductsDataFetchResponse,
  JobComplianceStop,
  JobComplianceStopProductData,
  JobComplianceStopService,
  JobEditCancelParams,
  JobEditChangeCustomerParams,
  JobLockResponse,
} from '@/models/JobComplianceTypes';
import { JobComplianceHoldResponse, JobComplianceProductLookupData } from '@/models/JobComplianceTypes';
import {
  bookJobWhToSWh,
  bookJobWhToWh,
  changeJobDate,
  completeStopFromDashboard,
  createJobStopData,
  editJobTicket,
  getAddressBookAddresses,
  getAddressLookup,
  getDashboardCustomer,
  getDashboardJobDetailed,
  getDashboardJobDetailedJob,
  getDriversRetransmit,
  getLookupProductModel,
  getLookupSuburb,
  getPriceLeg,
  getZammadEmailLookup,
  removeJobStop3,
  setJobAddStairs,
  setJobAllocation,
  setJobAsCancel,
  setJobAsFutile,
  setJobCustomerCode,
  setJobCustomerDetails,
  setJobEditAllocation,
  setJobEditAsCancel,
  setJobEditAsFutile,
  setJobLock,
  setJobOnHold,
  setJobStopData,
  setJobStopProducts,
  setJobStopServices,
  setJobUnlock,
  setScheduleStatus,
  unsetJobOnHold,
  updateProfile,
} from '@/api';
import { notifyNetworkError, notifyNetworkSuccess } from '@/helpers/notifiers';

import { AddressBookLookupData } from '@/models/addressBook';
import { DriverRetransmit } from '@/models/reports';
import { ISelectSuburb } from '@/models/jbAdvanced';
import { JobChangeDateParams } from '@/models/scheduler';
import { JobSchema } from '@/models/jobData';
import { JobTicketResponce } from '@/models/jobTicket';
import { isCancel } from 'axios';
import { sendError } from '@/helpers/sentry';

export const validateOKStatusReqConf = { validateStatus: (status: number) => status === 200 };
export const validateSchedulerAllocationsSuccesReqConf = { validateStatus: (status: number) => status === 201 };
const validateJobBookingStatusReqConf = {
  validateStatus: (status: number) => {
    return [200, 201, 400].includes(status);
  },
};
const validateJobLockStatusReqConf = {
  validateStatus: (status: number) => {
    return [200, 403, 409].includes(status);
  },
};

const validateJobEditStatusReqConf = {
  validateStatus: (status: number) => {
    return [200, 201, 400, 403, 409].includes(status);
  },
};

function notifyNetworkErrorDefault(err: AxiosError<customAPIError400>, title: string) {
  if (err.response?.data?.error) {
    notifyNetworkError({
      title: title,
      text: err.response?.data?.error.join(', '),
    });
  } else {
    notifyNetworkError({
      title: 'Something went wrong',
    });
  }
}

function getOnDetailedJobRejected(notificationTitle: string, notificationText?: string) {
  return function onDetailedJobRejected(err: AxiosError) {
    // example `err.response?.status`: 500, 404, 403, 401, 400
    if (err.response?.status === 400) {
      notifyNetworkError({ title: notificationTitle, text: notificationText });
      return null;
    }
    if (isCancel(err)) {
      return null;
    }
    throw err;
  };
}

function getOnReferencesParamsRejected(notificationTitle: string, notificationText?: string) {
  return function onReferencesParamsRejected(err: AxiosError) {
    if (!isCancel(err)) {
      notifyNetworkError({ title: notificationTitle, text: notificationText, duration: 3000 });
      sendError(err);
    }
    throw err;
  };
}

export function fetchDetailedJob(jobId: number, jobDate?: string) {
  let resPromise: Promise<AxiosResponse>;
  if (!jobDate) {
    resPromise = getDashboardJobDetailedJob(jobId, validateOKStatusReqConf);
  } else {
    resPromise = getDashboardJobDetailed(jobId, jobDate, validateOKStatusReqConf);
  }
  return resPromise.then(res => res.data).catch(getOnDetailedJobRejected('Error getting the job'));
}

export async function fetchJobLock(jobId: number, jobDate: string) {
  return setJobLock(jobId, jobDate, validateOKStatusReqConf)
    .then(res => {
      return res.data as JobLockResponse;
    })
    .catch(err => {
      if (!isCancel(err)) {
        sendError(err, 'fetchJobLock');
      }
      return false;
    });
}

export function fetchReferencesParams(customerCode: string): Promise<ReferencesParams> {
  return getDashboardCustomer(customerCode, validateOKStatusReqConf)
    .then(res => res.data)
    .catch(
      getOnReferencesParamsRejected(
        "Error getting Customer's profile info",
        'Something went wrong while make a request',
      ),
    );
}

/**
 * This function is used to book a job from the warehouse to warehouse.
 * Resolves to an array with two elements:
 * - The ID of the booked job.
 * - The date of the booked job.
 * Rejects with the custom BookingError if the request status code 400.
 * If the request fails due to an unknown error, it shows a notification and throws error.
 * The catch() should come first as we throw BookingError and what to catch it in the caller function.
 */
export async function fetchBookJobWhToWh(jobData: JobSchema[]): Promise<(string | number)[]> {
  return await bookJobWhToWh(jobData, validateJobBookingStatusReqConf)
    .catch(err => {
      if (!isCancel(err)) {
        // If the error is due to an unknown reason, show a notification and throw the error
        notifyNetworkError({ title: 'Unknown error during job booking' });
      }
      throw err;
    })
    .then(res => {
      // If the error is due to a failed request with 400 status, return an array with the error reason
      if (res.status === 400 && res.data.failed.length > 0) {
        throw new BookingError(res.data.failed[0].error_reason);
      }
      return [res.data.booked[0].rh_id, res.data.booked[0].rh_date];
    });
}

/**
 * This function is used to book a job from the warehouse to SAME warehouse.
 * Resolves to an array with two elements:
 * - The ID of the booked job.
 * - The date of the booked job.
 * Rejects with the custom BookingError if the request status code 400.
 * If the request fails due to an unknown error, it shows a notification and throws error.
 * The catch() should come first as we throw BookingError and what to catch it in the caller function.
 */
export async function fetchBookJobWhToSameWh(jobData: JobSchema[]): Promise<(string | number)[]> {
  return await bookJobWhToSWh(jobData, validateJobBookingStatusReqConf)
    .catch(err => {
      if (!isCancel(err)) {
        // If the error is due to an unknown reason, show a notification and throw the error
        notifyNetworkError({ title: 'Unknown error during job booking' });
      }
      throw err;
    })
    .then(res => {
      // If failed request with 400 status, return the error reason
      if (res.status === 400 && res.data.failed.length > 0) {
        throw new BookingError(res.data.failed[0].error_reason);
      }
      return [res.data.booked[0].rh_id, res.data.booked[0].rh_date];
    });
}

export async function fetchGetAddressBookAddresses(params) {
  const notificationTitle = 'Error getting the Address Book';
  return getAddressBookAddresses(params, validateOKStatusReqConf)
    .then(res => res.data)
    .catch(err => {
      if (!isCancel(err)) {
        notifyNetworkError({ title: notificationTitle });
      }
      throw err;
    });
}

export async function fetchGetLookupSuburb(params): Promise<ISelectSuburb[]> {
  const notificationTitle = 'Error getting the Suburb';
  return getLookupSuburb(params, validateOKStatusReqConf)
    .then(res => res.data)
    .catch(err => {
      if (!isCancel(err)) {
        notifyNetworkError({ title: notificationTitle });
      }
      throw err;
    });
}

export async function fetchGetLookupProductModel(params): Promise<string[]> {
  const notificationTitle = 'Error getting the Model';
  return getLookupProductModel(params, validateOKStatusReqConf)
    .then(res => {
      const items: JobComplianceProductLookupData[] = res.data;
      return items.map(item => item.name);
    })
    .catch(err => {
      if (!isCancel(err)) {
        notifyNetworkError({ title: notificationTitle });
      }
      throw err;
    });
}

// Complete stop
export async function fetchCompleteStop(
  action: string,
  params: StopCompleteParams,
): Promise<AxiosResponse | AxiosStatusResponse | null> {
  return completeStopFromDashboard(action, params)
    .then(res => {
      if (res?.data.Result === 'Stop sent') {
        notifyNetworkSuccess({ text: 'The job was completed successfully' });
      }
      return res?.data;
    })
    .catch(err => {
      if (!isCancel(err)) {
        notifyNetworkError({ title: 'Failed to complete the job' });
      }
      throw err;
    });
}

export async function fetchSetJobEditAllocation(
  rockhopper_id: number,
  rockhopper_date: string,
  params: JobComplianceDriverAllocateParams,
): Promise<JobComplianceDataFetchResponse> {
  return setJobEditAllocation(rockhopper_id, rockhopper_date, params)
    .then(() => {
      switch (params.action) {
        case 'deallocate':
        case 'deallocate_driv':
          notifyNetworkSuccess({ text: 'The deallocation was successful' });
          break;
        case 'preallocate':
          notifyNetworkSuccess({ text: 'The pre-allocation was successful' });
          break;
        case 'allocate':
          notifyNetworkSuccess({ text: 'The allocation was successful' });
          break;
        default:
          notifyNetworkSuccess({ text: `The "${params.action}" was successful` });
      }
      return {
        status: 'ok',
        error: '',
      };
    })
    .catch(err => {
      if (!isCancel(err)) {
        switch (err.response?.status) {
          case 400:
            notifyNetworkError({ title: err.response.data || 'Something went wrong' });
            return {
              status: 'error',
              error: err.response?.data?.error,
            };
          case 403:
            return {
              status: 'editing',
            };
          case 404:
            notifyNetworkError({ title: 'Job not found' });
            return {
              status: 'error',
              error: 'Job not found',
            };
          case 409:
            return {
              status: 'unlocked',
            };
        }
        notifyNetworkError({ title: err.response?.data?.error || 'Something went wrong' });
        return {
          status: 'error',
          error: err.response?.data?.error || 'Something went wrong',
        };
      }
      throw err;
    });
}

// Split to base
export async function fetchSetJobStatusSplitToBase(params: JobComplianceDriverAllocateParams): Promise<AxiosResponse> {
  return setJobAllocation(params)
    .then(res => {
      notifyNetworkSuccess({ text: 'The job was successfully split to base' });
      return res.data;
    })
    .catch(err => {
      if (!isCancel(err)) {
        err.response?.status === 400
          ? notifyNetworkError({ text: err.response.data })
          : notifyNetworkError({ title: 'Error when splitting to base' });
      }
      throw err;
    });
}

export async function fetchZammadEmailLookup(value: string, signal: AbortSignal): Promise<string[]> {
  return getZammadEmailLookup(value, signal).then(res => {
    return res.data;
  });
}

function handleJobEditResponse(res: any, success_text) {
  switch (res.status) {
    case 400:
      return {
        status: 'error',
        error: res.data.error,
      };
    case 403:
      return {
        status: 'editing',
      };
    case 404:
      return {
        status: 'error',
        error: 'Job not found',
      };
    case 409:
      return {
        status: 'unlocked',
      };
    case 200:
      notifyNetworkSuccess({ text: success_text });
      return {
        status: 'ok',
        data: res.data,
      };
    case 201:
      notifyNetworkSuccess({ text: success_text });
      return {
        status: 'ok',
        job: res.data,
      };
    default:
      return {
        status: 'ok',
      };
  }
}

export async function fetchSetJobCustomerDetails(
  rockhopper_id: number,
  rockhopper_date: string,
  diff,
): Promise<JobComplianceDataFetchResponse> {
  return setJobCustomerDetails(rockhopper_id, rockhopper_date, diff, validateJobEditStatusReqConf)
    .catch(err => {
      if (!isCancel(err)) {
        const error_text = err.response?.data?.error || 'Something went wrong';
        notifyNetworkError({ text: error_text });
      }
      throw err;
    })
    .then(res => {
      return handleJobEditResponse(res, 'The customer details were successfully updated');
    });
}

export async function fetchSetJobRemarks(
  rockhopper_id: number,
  rockhopper_date: string,
  diff,
): Promise<JobComplianceDataFetchResponse> {
  return setJobCustomerDetails(rockhopper_id, rockhopper_date, diff, validateJobEditStatusReqConf)
    .catch(err => {
      if (!isCancel(err)) {
        const error_text = err.response?.data?.error || 'Something went wrong';
        notifyNetworkError({ text: error_text });
      }
      throw err;
    })
    .then(res => {
      return handleJobEditResponse(res, 'Remarks were successfully updated');
    });
}

export async function fetchCreateJobStopData(
  rockhopper_id: number,
  rockhopper_date: string,
  stop_number: number,
  diff,
): Promise<JobComplianceDataFetchResponse> {
  return createJobStopData(rockhopper_id, rockhopper_date, stop_number, diff)
    .catch(err => {
      if (!isCancel(err)) {
        const error_text = err.response?.data?.error || 'Something went wrong';
        notifyNetworkError({ text: error_text });
      }
      throw err;
    })
    .then(res => {
      return handleJobEditResponse(res, 'Stop data created successfully');
    });
}

export async function fetchSetJobStopData(
  rockhopper_id: number,
  rockhopper_date: string,
  stop_number: number,
  diff,
): Promise<JobComplianceDataFetchResponse> {
  return setJobStopData(rockhopper_id, rockhopper_date, stop_number, diff, validateJobEditStatusReqConf)
    .catch(err => {
      if (!isCancel(err)) {
        const error_text = err.response?.data?.error || 'Something went wrong';
        notifyNetworkError({ text: error_text });
      }
      throw err;
    })
    .then(res => {
      return handleJobEditResponse(res, 'Stop data updated successfully');
    });
}
export async function fetchSetJobStopProductData(
  rockhopper_id: number,
  rockhopper_date: string,
  stop_number: number,
  diff,
): Promise<JobComplianceProductsDataFetchResponse> {
  return setJobStopProducts(rockhopper_id, rockhopper_date, stop_number, diff, validateJobEditStatusReqConf)
    .catch(err => {
      if (!isCancel(err)) {
        const error_text = err.response?.data?.error || 'Something went wrong';
        notifyNetworkError({ text: error_text });
      }
      throw err;
    })
    .then(res => {
      return handleJobEditResponse(res, 'Product data updated successfully');
    });
}

export async function fetchSetJobStopServiceCodesData(
  rockhopper_id: number,
  rockhopper_date: string,
  stop_number: number,
  diff,
): Promise<JobComplianceDataFetchResponse> {
  return setJobStopServices(rockhopper_id, rockhopper_date, stop_number, diff, validateJobEditStatusReqConf)
    .catch(err => {
      if (!isCancel(err)) {
        const error_text = err.response?.data?.error || 'Something went wrong';
        notifyNetworkError({ text: error_text });
      }
      throw err;
    })
    .then(res => {
      return handleJobEditResponse(res, 'Service codes updated successfully');
    });
}

/**
 * @desc
 * Custom error interface for the 400 status code.
 * @example
 * { "non_field_errors": ["Job is not allocated"] }
 * { "driver": ["Driver field is required"] }
 */
interface customAPIError400 {
  [key: string]: string[];
}

/**
 * @desc
 * Handles the error response for the job edit requests.
 */
function handleJobEditErrorResponse(err: AxiosError<customAPIError400>) {
  switch (err.response?.status) {
    case 400:
      // We show only the first error message as usually there is only one
      // error message in the response. See customAPIError400 type example above.
      notifyNetworkError({ text: Object.values(err.response.data)[0][0] });
      break;
    case 403:
      notifyNetworkError({ text: 'Job is editing by another user' });
      break;
    case 404:
      notifyNetworkError({ text: 'Job not found' });
      break;
    case 409:
      notifyNetworkError({ text: 'Job must be locked, before trying to edit it' });
      break;
    default:
      notifyNetworkError({ text: err.response?.data?.error.join(', ') || 'Something went wrong' });
  }
  throw err;
}

export async function fetchAddStairsService(
  rockhopper_id: number,
  rockhopper_date: string,
  stop_number: number,
): Promise<boolean | string> {
  return setJobAddStairs(rockhopper_id, rockhopper_date, stop_number)
    .then(res => {
      if (res.status === 201) {
        notifyNetworkSuccess({ text: 'Stairs service has been successfully added' });
        return true;
      }
      return false;
    })
    .catch(err => {
      if (!isCancel(err)) {
        handleJobEditErrorResponse(err);
      }
      throw err;
    });
}

/**
 * Fetches the endpoint to set the job status as "Futile".
 *
 * @param {JobFutileParams} params - An object with the key `reason` and a value with the text of this reason
 * @returns {Promise<AxiosResponse>} A promise that resolves to the response data
 */
export async function fetchSetJobAsFutile(params: JobFutileParams): Promise<AxiosResponse> {
  return setJobAsFutile(params, validateOKStatusReqConf)
    .then(res => {
      notifyNetworkSuccess({ text: 'The job was successfully closed as futile' });
      return res.data;
    })
    .catch(err => {
      if (!isCancel(err)) {
        if (err.response?.status === 400 && err.response.data.non_field_errors[0] === 'Not allocated') {
          notifyNetworkError({ text: 'The job is not allocated' });
        } else {
          notifyNetworkError({ title: 'Error when closing a job as futile' });
        }
      }
      throw err;
    });
}

// Cancel the job
export async function fetchSetJobCancel(params: JobCancelParams): Promise<AxiosResponse> {
  return setJobAsCancel(params, validateOKStatusReqConf)
    .catch(err => {
      if (!isCancel(err)) {
        if (err.response?.status === 400) {
          notifyNetworkError({
            text: err.response.data.non_field_errors[0]
              ? err.response.data.non_field_errors[0]
              : 'The job is not allocated',
          });
        } else {
          notifyNetworkError({ title: 'Error when canceling a job' });
        }
      }
      throw err;
    })
    .then(res => {
      notifyNetworkSuccess({ text: 'The job was successfully canceled' });
      return res.data;
    });
}

export async function fetchSetTicketState(ticketData, state: string, state_id: number): Promise<JobTicketResponce> {
  const notificationTitle = 'Error closing the ticket';
  const newTicketData: JobTicketResponce = { ...ticketData, state: state, state_id: state_id };

  return await editJobTicket(newTicketData)
    .then(res => {
      return res;
    })
    .catch(err => {
      if (!isCancel(err)) {
        notifyNetworkError({ title: notificationTitle });
      }
      throw err;
    });
}

export async function fetchSetJobOnHold(jobId: number, jobDate: string): Promise<JobComplianceHoldResponse> {
  return setJobOnHold(jobId, jobDate)
    .catch(err => {
      if (!isCancel(err)) {
        notifyNetworkError({
          text: err.response?.data?.error ? err.response?.data?.error : 'Failed to set job on hold',
        });
      }
      throw err;
    })
    .then(res => {
      return res;
    });
}

export async function fetchUnsetJobOnHold(jobId: number, jobDate: string): Promise<JobComplianceHoldResponse> {
  return unsetJobOnHold(jobId, jobDate)
    .catch(err => {
      if (!isCancel(err)) {
        notifyNetworkError({
          text: err.response?.data?.error ? err.response?.data?.error : 'Failed to unset job from hold',
        });
      }
      throw err;
    })
    .then(res => {
      return res;
    });
}

export async function fetchGetAddressLookup(code: string): Promise<AddressBookLookupData[]> {
  return await getAddressLookup(code)
    .then(res => {
      return res.data;
    })
    .catch(err => {
      if (!isCancel(err)) {
        notifyNetworkError({ title: 'An error occurred while receiving addresses' });
      }
      throw err;
    });
}

/**
 * Fetches the endpoint to lock the job.
 *
 * @param id - rockhopper_id
 * @param date - rockhopper_date
 */
export async function fetchSetJobLock(id: number, date: string): Promise<JobLockResponse> {
  return await setJobLock(id, date, validateJobLockStatusReqConf)
    .catch(err => {
      if (!isCancel(err)) {
        handleJobEditErrorResponse(err);
      }
      throw err;
    })
    .then(res => {
      // If Job is locked by Rockhopper or Scheduler app it will return an error text with
      // the name of the user/application by which job was locked.
      if (res.status === 409) {
        notifyNetworkError({ text: res.data.error || 'Something went wrong' });
        throw new JobLockError(res.data.error as string);
      }
      if (res.status === 403) {
        const user = res.data.locked_by ?? 'unknown';
        notifyNetworkError({ text: 'Job is editing by another user: ' + user });
        throw new JobLockByUserError(user);
      }
      return res.data;
    });
}

/**
 * Fetches the endpoint to unlock the job.
 *
 * @param id - rockhopper_id
 * @param date - rockhopper_date
 */
export async function fetchJobUnlock(id: number, date: string): Promise<JobLockResponse | void> {
  return await setJobUnlock(id, date)
    .then(res => res.data)
    .catch(err => {
      if (!isCancel(err)) {
        // If the error is due to an unknown reason, show a notification and throw the error
        sendError(err, 'fetchJobUnlock');
      }
      throw err;
    });
}

/**
 * Fetches the endpoint to set the job as "futile".
 *
 * @param id - rockhopper_id
 * @param date - rockhopper_date
 * @param params – An object with the key `reason`
 */
export async function fetchSetJobEditAsFutile(
  id: number,
  date: string,
  params: { [key: string]: string },
): Promise<void> {
  return await setJobEditAsFutile(id, date, params, validateOKStatusReqConf)
    .then(res => {
      notifyNetworkSuccess({ text: 'The job was successfully closed as futile' });
      return res.data;
    })
    .catch(err => {
      if (!isCancel(err)) {
        // In case of validation error (status 400) we need to throw a custom error
        //  to allow component to handle it properly. Most of the time should send `unlock`.
        if (err.response?.status === 400) {
          const msg = (Object.values(err.response?.data as customAPIError400)[0] as string[])[0];
          throw new ValidationAPIError(msg);
        }
        handleJobEditErrorResponse(err);
      }
      throw err;
    });
}

/**
 * Fetches the endpoint to "cancel" the job.
 *
 * @param id - rockhopper_id
 * @param date - rockhopper_date
 * @param params - An object with the keys `name` and `reason`
 */
export async function fetchSetJobEditCancel(id: number, date: string, params: JobEditCancelParams): Promise<void> {
  return await setJobEditAsCancel(id, date, params, validateOKStatusReqConf)
    .then(res => {
      notifyNetworkSuccess({ text: 'The job was successfully canceled' });
      return res.data;
    })
    .catch(err => {
      if (!isCancel(err)) {
        // In case of validation error (status 400) we need to throw a custom error
        //  to allow component to handle it properly. Most of the time should send `unlock`.
        if (err.response?.status === 400) {
          const msg = (Object.values(err.response?.data as customAPIError400)[0] as string[])[0];
          throw new ValidationAPIError(msg);
        }
        handleJobEditErrorResponse(err);
      }
      throw err;
    });
}

/**
 * Fetches the endpoint to set the job status as "Split to Base". Used in the JobEdit modal window.
 *
 * @param id - rockhopper_id
 * @param date - rockhopper_date
 * @param params - An object with the keys `action`, `driver`,
 * `base` and `stop_number`
 */
export async function fetchSetJobEditStatusSplitToBase(
  id: number,
  date: string,
  params: JobComplianceDriverAllocateParams,
) {
  return await setJobEditAllocation(id, date, params)
    .then(res => {
      notifyNetworkSuccess({ text: 'The job was successfully split to base' });
      return res.data;
    })
    .catch(err => {
      if (!isCancel(err)) {
        // In case of validation error (status 400) we need to throw a custom error
        //  to allow component to handle it properly. Most of the time should send `unlock`.
        if (err.response?.status === 400) {
          const msg = (Object.values(err.response?.data as customAPIError400)[0] as string[])[0];
          throw new ValidationAPIError(msg);
        }
        handleJobEditErrorResponse(err);
      }
      throw err;
    });
}

/**
 * Fetches the endpoint to delete the 3rd stop. Used in the JobEdit modal window.
 *
 * @param id - rockhopper_id
 * @param date - rockhopper_date
 * @param stop_number
 * @param params - An object (could be empty)
 */
export async function fetchRemove3rdStop(
  id: number,
  date: string,
  stop_number: number,
  params,
): Promise<string | void> {
  return await removeJobStop3(id, date, stop_number, params)
    .then(res => {
      notifyNetworkSuccess({ text: 'The third stop was successfully deleted' });
      return res.data;
    })
    .catch(err => {
      if (!isCancel(err)) {
        // In case of validation error (status 400) we need to throw a custom error
        //  to allow component to handle it properly. Most of the time should send `unlock`.
        if (err.response?.status === 400) {
          const msg = (Object.values(err.response?.data as customAPIError400)[0] as string[])[0];
          throw new ValidationAPIError(msg);
        }
        handleJobEditErrorResponse(err);
      }
      throw err;
    });
}

/**
 * Fetches the endpoint to change the "Can Schedule" status of the job, It is called
 * by a toggles on the JobDetail page and in the JobEdit modal window.
 *
 * @param value - "Can Schedule" toggle new value
 * @param id - rockhopper_id
 * @param date - rockhopper_date
 */
export async function fetchCanScheduleChange(
  value: boolean,
  id: number,
  date: string,
): Promise<AxiosStatusResponse | void> {
  return await setScheduleStatus(value, id, date)
    .then(res => {
      notifyNetworkSuccess({ text: 'Successfully changed the "Can schedule" status' });
      return res.data;
    })
    .catch(err => {
      if (!isCancel(err)) {
        if (err.response?.status === 403) {
          // If the job is locked by another user, we need to show a notification
          notifyNetworkError({ text: err.response?.data?.error });
        } else {
          notifyNetworkErrorDefault(err, 'Error changing the schedule status');
        }
      }
      throw err;
    });
}

/**
 * Updates user profile via API call
 */
export async function fetchUpdateProfile(formData: FormData): Promise<AxiosResponse | void> {
  return await updateProfile(formData)
    .then(res => {
      if (res.status === 200) {
        return res;
      }
      return Promise.reject(res);
    })
    .catch(err => {
      if (!isCancel(err)) {
        notifyNetworkError({
          title: 'Error updating the profile',
        });
      }
      throw err;
    });
}

export async function fetchJobPriceLeg(
  id: number,
  date: string,
  leg: number,
): Promise<JobComplianceStopService[] | void> {
  return await getPriceLeg(id, date, leg)
    .then(res => {
      return res.data;
    })
    .catch(err => {
      if (!isCancel(err)) {
        if (err.response?.status === 403) {
          notifyNetworkError({ text: err.response?.data?.error });
        } else {
          notifyNetworkErrorDefault(err, 'Error getting job price');
        }
      }
      throw err;
    });
}

export async function fetchDriverRetransmit(date: string): Promise<DriverRetransmit[] | void> {
  return await getDriversRetransmit(date)
    .then(res => {
      return res.data;
    })
    .catch(err => {
      if (!isCancel(err)) {
        notifyNetworkErrorDefault(err, 'Error getting list of drivers');
      }
      throw err;
    });
}

export async function fetchSetJobCustomerCode(
  id: number,
  date: string,
  params: JobEditChangeCustomerParams,
): Promise<JobComplianceDataFetchResponse> {
  return await setJobCustomerCode(id, date, params, validateJobEditStatusReqConf)
    .then(res => {
      notifyNetworkSuccess({ text: 'Customer code was successfully changed' });
      return res.data;
    })
    .catch(err => {
      if (!isCancel(err)) {
        if (err.response?.status === 400) {
          const msg = (Object.values(err.response?.data as customAPIError400)[0] as string[])[0];
          throw new ValidationAPIError(msg);
        }
        handleJobEditErrorResponse(err);
      }
      throw err;
    });
}
